import React, { useState } from 'react';
import { ExternalLink, Copy, Chrome, RefreshCw, AlertCircle } from 'lucide-react';
import { openInExternalBrowser, getInstructionMessage, isInAppBrowser } from '../browserDetection';

const InAppBrowserScreen = () => {
  const [copied, setCopied] = useState(false);
  const currentUrl = window.location.href;
  const browserInfo = isInAppBrowser();

  const handleOpenExternal = () => {
    openInExternalBrowser(currentUrl);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Fallback للأجهزة القديمة
      const textArea = document.createElement('textarea');
      textArea.value = currentUrl;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Fallback copy failed:', err);
      }
      document.body.removeChild(textArea);
    }
  };

  const getPlatformIcon = () => {
    // يمكنك استبدال هذا بأيقونات خاصة بكل منصة
    return <Chrome className="w-16 h-16 text-blue-500" />;
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
        {/* أيقونة */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            <div className="absolute inset-0 bg-blue-100 rounded-full blur-xl opacity-50"></div>
            <div className="relative bg-gradient-to-br from-blue-100 to-indigo-100 p-4 rounded-full">
              {getPlatformIcon()}
            </div>
          </div>
        </div>

        {/* العنوان */}
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <AlertCircle className="w-4 h-4" />
            <span>متصفح {browserInfo.platform || 'داخلي'}</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            افتح الموقع في متصفح خارجي
          </h1>
          <p className="text-gray-600 leading-relaxed">
            {getInstructionMessage() || 'للحصول على أفضل تجربة وتجنب المشاكل التقنية، يرجى فتح الموقع في متصفح خارجي مثل Safari أو Chrome'}
          </p>
        </div>

        {/* الأزرار */}
        <div className="space-y-3 mb-6">
          {/* زر الفتح في متصفح خارجي */}
          <button
            onClick={handleOpenExternal}
            className="w-full flex items-center justify-center gap-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-4 px-6 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
          >
            <ExternalLink className="w-5 h-5" />
            افتح في متصفح خارجي
          </button>

          {/* زر نسخ الرابط */}
          <button
            onClick={handleCopyLink}
            className="w-full flex items-center justify-center gap-3 bg-gray-100 hover:bg-gray-200 text-gray-800 py-3 px-6 rounded-xl font-semibold transition-all duration-200"
          >
            <Copy className="w-4 h-4" />
            {copied ? '✓ تم النسخ!' : 'نسخ الرابط'}
          </button>
        </div>

        {/* تعليمات إضافية */}
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-right">
          <h3 className="font-bold text-blue-900 text-sm mb-2 flex items-center gap-2">
            <span className="bg-blue-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs">ℹ</span>
            كيفية الفتح في متصفح خارجي:
          </h3>
          <ol className="text-blue-800 text-sm space-y-2">
            {navigator.userAgent.includes('iPhone') || navigator.userAgent.includes('iPad') ? (
              <>
                <li className="flex gap-2">
                  <span className="text-blue-500 font-bold">1.</span>
                  <span>اضغط على زر "افتح في متصفح خارجي" أعلاه</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-blue-500 font-bold">2.</span>
                  <span>أو انسخ الرابط والصقه في Safari</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-blue-500 font-bold">3.</span>
                  <span>أو اضغط على النقاط الثلاث (...) واختر "فتح في Safari"</span>
                </li>
              </>
            ) : (
              <>
                <li className="flex gap-2">
                  <span className="text-blue-500 font-bold">1.</span>
                  <span>اضغط على زر "افتح في متصفح خارجي" أعلاه</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-blue-500 font-bold">2.</span>
                  <span>أو انسخ الرابط والصقه في Chrome</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-blue-500 font-bold">3.</span>
                  <span>أو اضغط على النقاط الثلاث (⋮) واختر "فتح في المتصفح"</span>
                </li>
              </>
            )}
          </ol>
        </div>

        {/* زر المحاولة رغم ذلك (اختياري) */}
        <button
          onClick={() => window.location.reload()}
          className="mt-6 text-gray-500 hover:text-gray-700 text-sm font-medium underline flex items-center justify-center gap-2 w-full py-2 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          المتابعة على أي حال (قد تواجه مشاكل)
        </button>
      </div>

      {/* معلومات إضافية في الأسفل */}
      <div className="mt-6 text-center text-gray-500 text-xs max-w-md">
        <p>
          بعض المميزات قد لا تعمل بشكل صحيح في متصفحات التطبيقات الداخلية.
          للحصول على أفضل تجربة، استخدم متصفح خارجي.
        </p>
      </div>
    </div>
  );
};

export default InAppBrowserScreen;